import type { TimesheetEntry } from "../types.ts";

export type ProjectAllocationRow = {
  projectId: number;
  employeeId: number;
  allotment: number;
  consumed: number;
  remainingBal: number;
  status: string;
};

export function getAllocationForProject(
  allocations: ProjectAllocationRow[] | undefined,
  projectId: number,
): ProjectAllocationRow | undefined {
  return allocations?.find((a) => a.projectId === projectId);
}

export function minutesUsedForProjectInEntries(
  entries: TimesheetEntry[],
  projectId: number,
  excludeEntryId?: string,
): number {
  return entries
    .filter(
      (e) =>
        e.projectId != null &&
        e.projectId === projectId &&
        (excludeEntryId == null || e.id !== excludeEntryId),
    )
    .reduce(
      (acc, e) => acc + e.hours * 60 + Number(e.minutes || 0),
      0,
    );
}

export function entryTotalMinutes(
  hours: number | string,
  minutes: string | number,
): number {
  return Number(hours || 0) * 60 + Number(minutes || 0);
}

export function exceedsProjectRemainingBal(params: {
  projectId: number | null | undefined;
  titleType?: "PROJECT" | "OTHERS";
  entryMinutes: number;
  otherEntriesMinutesForSameProject: number;
  remainingBal: number | undefined;
}): boolean {
  const {
    projectId,
    titleType,
    entryMinutes,
    otherEntriesMinutesForSameProject,
    remainingBal,
  } = params;
  if (titleType === "OTHERS" || projectId == null) return false;
  if (remainingBal == null) return false;
  return otherEntriesMinutesForSameProject + entryMinutes > remainingBal;
}

/** Returns an error message if any project's total time exceeds `remainingBal`, else null. */
export function getAllocationSubmitError(
  entries: TimesheetEntry[],
  allocations: ProjectAllocationRow[] | undefined,
): string | null {
  if (!allocations?.length) return null;
  const totals = new Map<number, number>();
  for (const e of entries) {
    if (e.titleType === "OTHERS" || e.projectId == null) continue;
    const m = e.hours * 60 + Number(e.minutes || 0);
    totals.set(e.projectId, (totals.get(e.projectId) ?? 0) + m);
  }
  for (const [pid, total] of totals) {
    const row = getAllocationForProject(allocations, pid);
    if (row && total > row.remainingBal) {
      return "Total time for one or more projects exceeds remaining allocation.";
    }
  }
  return null;
}
